import { Button, Combobox, type ComboboxItem, Textarea } from '@ibsys/kit';
import { ScissorsIcon } from 'lucide-react';
import { FC, type SyntheticEvent, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConversation } from '@/entities/conversations';
import { getSpeakerLabel } from '../speaker';
import { splitPhraseText } from './recordForm';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import type { TranscriptRecordFormValues } from './types';

type Props = {
  /** Вынос выделенного фрагмента в новую реплику; без обработчика действия нет (у новой реплики) */
  onSplit?: (split: { fragment: string; rest: string }) => void;
  /** Фрагмент уже вынесен и правится рядом: повторный вынос недоступен */
  isSplitOpen: boolean;
};

/**
 * Говорящий и текст реплики. Говорящий выбирается из участников диалога.
 */
export const TranscriptPhraseFields: FC<Props> = ({ onSplit, isSplitOpen }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { control } = useFormContext<TranscriptRecordFormValues>();
  const { conversationId } = useTranscriptEditorContext();
  const { data: conversation } = useConversation(conversationId);
  const text = useWatch({ control, name: 'text' });
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const speakerOptions = useMemo<ComboboxItem[]>(
    () => (conversation?.persons ?? []).map((person) => ({ value: person.id, label: getSpeakerLabel(person, t) })),
    [conversation?.persons, t],
  );

  const split = splitPhraseText(text, selection.start, selection.end);
  const canSplit = split !== null && !isSplitOpen;

  const handleSelect = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    setSelection({ start: selectionStart, end: selectionEnd });
  };

  return (
    <div className="flex flex-col gap-3">
      <Combobox
        control={control}
        name="personId"
        options={speakerOptions}
        label={t('dialogs:transcriptEdit.speaker')}
        placeholder={t('dialogs:transcriptEdit.speakerPlaceholder')}
        required
      />
      <Textarea
        control={control}
        name="text"
        label={t('dialogs:transcriptEdit.text')}
        rows={2}
        required
        onSelect={handleSelect}
      />
      {onSplit && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          icon={<ScissorsIcon />}
          // На узкой форме подпись переносится внутри рамки формы, а не вылезает за неё (кнопки kit
          // не переносят подпись и держат постоянную высоту); одной строкой — обычная высота sm
          className="self-start h-auto min-h-6 py-px whitespace-normal text-left"
          disabled={!canSplit}
          onClick={() => split && onSplit(split)}
        >
          {t('dialogs:transcriptEdit.splitFragment')}
        </Button>
      )}
    </div>
  );
};
